import React, { useState, useEffect } from 'react';

// MUI Components
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@mui/material';

// MUI Icons
import {
  Add as AddIcon,
  Close as CloseIcon,
  InfoOutlined
} from '@mui/icons-material';

// Contexts
import { useTheme } from '../../contexts/ThemeContext';

// Components
import CustomInput from '../Inputs/CustomInput';
import HtmlTooltip from '../Tooltip/HtmlTooltip';
import ImagesModal from '../Modals/ImagesModal';

const CustomSelect = ({ children, ...props }) => {
  const { theme } = useTheme();

  const style = {
    formControl: {
      "& .MuiOutlinedInput-root": {
        "& fieldset": {
          borderColor: theme.text.primary, // Updated border color
        },
        "&:hover fieldset": {
          borderColor: theme.text.primary, // Updated hover border color
        },
        "&.Mui-focused fieldset": {
          borderColor: theme.text.primary, // Updated focused border color
        },
      },
      "& .MuiInputLabel-root": {
        color: theme.text.primary, // Updated label color
        fontFamily: theme.fontFamily,
        "&.Mui-focused": {
          color: theme.text.primary, // Updated focused label color
        },
      },
      "& .MuiSelect-select": {
        fontFamily: theme.fontFamily,
        color: theme.text.primary, // Updated select text color
      },
      '& .MuiSelect-icon': {
        color: theme.text.primary, // Set chevron icon color
      },
      "& .MuiInputLabel-outlined.Mui-error": {
        color: theme.accent.error,
      },
      "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.accent.error,
      },
    },
    inputLabel: {
      fontFamily: theme.fontFamily,
      color: theme.text.primary,
      '&.Mui-focused': {
        color: theme.color.primary, // Change label color on focus
      },
    },
    select: {
      color: theme.text.primary,
      fontFamily: theme.fontFamily,
      '& .MuiSelect-icon': {
        color: theme.text.primary, // Set chevron icon color
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary, // Set border color
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.text.primary, // Set border color on hover
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.color.primary, // Set border color when focused
      },
    }
  }

  return (
    <FormControl variant="outlined" fullWidth sx={style.formControl} error={props.value === ""}>
      <InputLabel sx={style.inputLabel}>{props.value === "" ? `${props.label} *` : props.label}</InputLabel>
      <Select sx={style.select} {...props} fullWidth size="small" label={props.value === "" ? `${props.label} *` : props.label}>
        {children}
      </Select>
    </FormControl>
  );
};

const SchemaForm = ({ schema, currProduct, productType, formData, setFormData, isConvert }) => {
  const { theme } = useTheme();

  const [requiredFields, setRequiredFields] = useState({});
  const [openImgModal, setOpenImgModal] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [imageField, setImageField] = useState('');

  useEffect(() => {
    // Initialize form when schema and productType are available
    if (schema && schema.properties && productType) {
      // Initialize required fields
      updateRequiredFields();

      // Initialize form data with default values if not provided from parent
      if (!formData || Object.keys(formData).length === 0) {
        initializeFormData();
      }
    }
  }, [schema, productType]);

  // Listen for form data changes to update required fields based on conditions
  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      updateRequiredFields();
    }
  }, [formData]);

  function extractProductNetContent(title) {
    const validUnits = [
      "Count", "Liter", "Fluid Ounces", "Milliliter", "Kilogram",
      "Quart", "Centiliter", "Ounce", "Gram", "Each",
      "Gallon", "Pound", "Quart Dry", "Pint"
    ];

    // Map of common abbreviations to their full unit names from the enum
    const unitAbbreviations = {
      // Ounce variations
      'oz': 'Ounce',
      'ounce': 'Ounce',
      'ounces': 'Ounce',

      // Fluid Ounce variations
      'fl oz': 'Fluid Ounces',
      'fl. oz': 'Fluid Ounces',
      'fl.oz': 'Fluid Ounces',
      'floz': 'Fluid Ounces',
      'fluid oz': 'Fluid Ounces',
      'fluid ounce': 'Fluid Ounces',
      'fluid ounces': 'Fluid Ounces',

      // Milliliter variations
      'ml': 'Milliliter',
      'milliliter': 'Milliliter',
      'milliliters': 'Milliliter',
      'millilitre': 'Milliliter',
      'millilitres': 'Milliliter',

      // Liter variations
      // 'l': 'Liter',
      'liter': 'Liter',
      'liters': 'Liter',
      'litre': 'Liter',
      'litres': 'Liter',

      // Kilogram variations
      'kg': 'Kilogram',
      'kgs': 'Kilogram',
      'kilogram': 'Kilogram',
      'kilograms': 'Kilogram',
      'kilo': 'Kilogram',
      'kilos': 'Kilogram',

      // Gram variations
      // 'g': 'Gram',
      'gr': 'Gram',
      'gram': 'Gram',
      'grams': 'Gram',
      'gramme': 'Gram',
      'grammes': 'Gram',

      // Pound variations
      'lb': 'Pound',
      'lbs': 'Pound',
      'pound': 'Pound',
      'pounds': 'Pound',
      '#': 'Pound',

      // Quart variations
      'qt': 'Quart',
      'qts': 'Quart',
      'quart': 'Quart',
      'quarts': 'Quart',

      // Quart Dry variations
      'qt dry': 'Quart Dry',
      'qt. dry': 'Quart Dry',
      'quart dry': 'Quart Dry',
      'dry qt': 'Quart Dry',
      'dry quart': 'Quart Dry',

      // Gallon variations
      'gal': 'Gallon',
      'gals': 'Gallon',
      'gallon': 'Gallon',
      'gallons': 'Gallon',

      // Pint variations
      // 'pt': 'Pint',
      'pts': 'Pint',
      'pint': 'Pint',
      'pints': 'Pint',

      // Centiliter variations
      // 'cl': 'Centiliter',
      'centiliter': 'Centiliter',
      'centiliters': 'Centiliter',
      'centilitre': 'Centiliter',
      'centilitres': 'Centiliter',

      // Count/Each variations
      // 'ct': 'Count',
      'count': 'Count',
      'counts': 'Count',
      // 'ea': 'Each',
      'each': 'Each',
      'piece': 'Each',
      'pieces': 'Each',
      // 'pc': 'Each',
      'pcs': 'Each',
      'pack': 'Count',
      'packs': 'Count',
      // 'pk': 'Count',
      'pks': 'Count'
    };

    // Updated regex to handle hyphens between number and unit
    const measureRegex = /(\d+(?:\.\d+)?)\s*[-\s]*([\w\s]+)/i;
    const matches = title.match(measureRegex);

    if (matches) {
      const measure = parseFloat(matches[1]);
      // Clean up the matched unit by trimming and converting to lowercase
      let unit = matches[2].trim().toLowerCase();

      // Check if the unit is an abbreviation and map it to the full name
      if (unitAbbreviations[unit]) {
        return {
          productNetContentUnit: unitAbbreviations[unit],
          productNetContentMeasure: measure
        };
      }

      // Handle special cases with combined terms like "fl oz"
      for (const [abbr, fullUnit] of Object.entries(unitAbbreviations)) {
        if (unit.includes(abbr)) {
          return {
            productNetContentUnit: fullUnit,
            productNetContentMeasure: measure
          };
        }
      }

      // Direct match against the enum (case-insensitive)
      const directMatch = validUnits.find(
        validUnit => validUnit.toLowerCase() === unit ||
          validUnit.toLowerCase() === unit + 's' // Handle plurals
      );

      if (directMatch) {
        return {
          productNetContentUnit: directMatch,
          productNetContentMeasure: measure
        };
      }
    }

    return {
      productNetContentUnit: "Each",
      productNetContentMeasure: 1
    };
  };

  function extractAgeInfo(ageString) {
    // Using a regular expression to extract the number and unit
    const regex = /(\d+)\s+([A-Za-z]+)/;
    const match = ageString.match(regex);

    if (match) {
      return {
        unit: match[2].toLowerCase(),
        measure: parseInt(match[1])
      };
    }

    return {
      unit: "years",
      measure: null
    };
  };

  function parseVoltsContent(content) {
    if (!content) return [];

    // Check if the string contains " V"
    if (content.includes(" V")) {
      const parsedContent = content.split(", ");

      return parsedContent.map(volts => {
        // Match format like "1.5 V MaterialName" or just "1.5 V"
        const match = volts.match(/(\d+(?:\.\d+)?)\s*V\b/);
        if (match) {
          return {
            measure: parseFloat(match[1]),
            unit: "V"
          };
        }
        return null;
      }).filter(item => item !== null);
    } else {
      // Handle strings without explicit " V"
      const results = content.split(", ");
      return results.map(result => {
        return {
          measure: result.trim(),
          unit: "V"
        };
      });
    }
  }

  function parseFabricContent(content) {
    // Check if the string contains percentages
    if (content?.includes("%")) {
      // Split the string by commas to get individual material entries
      const materials = content.split(", ");

      // Map each material entry to an object with the required format
      return materials.map(material => {
        // Extract percentage and material name using regex
        const match = material.match(/(\d+\.\d+)% (.+)/);
        if (match) {
          const percentage = parseFloat(match[1]);
          const name = match[2];
          return {
            materialName: name,
            materialPercentage: percentage
          };
        }
        return null;
      }).filter(item => item !== null);
    } else {
      // Handle strings without percentages
      const materials = content?.split(", ") || [];

      // Set materialPercentage to null for each material
      return materials.map(material => {
        return {
          materialName: material.trim(),
          materialPercentage: null
        };
      });
    }
  }

  const initializeFormData = () => {
    // Use the schema directly to determine required fields instead of relying on requiredFields state
    const requiredList = schema.properties[productType]?.required || [];

    const initialData = {
      [productType]: {
        mainImageUrl: currProduct?.image || '',
        isProp65WarningRequired: currProduct?.prop65WarningText ? "Yes" : "No",
        productName: currProduct?.title || currProduct?.productName || '',
      }
    };
    if (!isConvert) {
      initialData[productType].keyFeatures = currProduct.keyFeatures || ['', '', ''];
      initialData[productType].shortDescription = currProduct?.description || "";
    }
    if (currProduct?.prop65WarningText !== "") {
      initialData[productType].prop65WarningText = currProduct?.prop65WarningText;
    }

    // Check for fields in the required list directly from schema
    if (requiredList.includes('assembledProductWeight')) {
      initialData[productType].assembledProductWeight = {
        measure: currProduct?.weight ? parseFloat(currProduct.weight.toFixed(2) || 0) : 0.00,
        unit: 'lb'
      };
    }
    if (requiredList.includes('assembledProductDepth')) {
      initialData[productType].assembledProductDepth = {
        measure: currProduct['length'] ? parseFloat(currProduct['length'].toFixed(2) || 0) : 0.00,
        unit: 'in'
      };
    }
    if (requiredList.includes('assembledProductHeight')) {
      initialData[productType].assembledProductHeight = {
        measure: currProduct?.height ? parseFloat(currProduct.height.toFixed(2) || 0) : 0.00,
        unit: 'in'
      };
    }
    if (requiredList.includes('assembledProductWidth')) {
      initialData[productType].assembledProductWidth = {
        measure: currProduct?.width ? parseFloat(currProduct.width.toFixed(2) || 0) : 0.00,
        unit: 'in'
      };
    }
    if (requiredList.includes('brand')) {
      initialData[productType].brand = currProduct?.brand || '';
    }
    if (requiredList.includes('material')) {
      // Fix the material handling to ensure it's always an array
      if (currProduct?.material) {
        // Check if it's already an array
        if (Array.isArray(currProduct.material)) {
          initialData[productType].material = [...currProduct.material];
        }
        // Check if it's an object that looks like an array (has numeric keys)
        else if (typeof currProduct.material === 'object') {
          // Convert the object to a proper array
          initialData[productType].material = Object.values(currProduct.material);
        }
        // Handle string or other types
        else {
          initialData[productType].material = [currProduct.material];
        }
      } else {
        initialData[productType].material = [];
      }
    }
    if (requiredList.includes('condition')) {
      initialData[productType].condition = "New";
    }
    if (requiredList.includes('has_written_warranty')) {
      initialData[productType].has_written_warranty = "No";
    }
    if (requiredList.includes('smallPartsWarnings')) {
      initialData[productType].smallPartsWarnings = ["0 - No warning applicable"];
    }
    if (requiredList.includes('manufacturer')) {
      initialData[productType].manufacturer = currProduct?.manufacturer || '';
    }
    if (requiredList.includes('manufacturerPartNumber')) {
      initialData[productType].manufacturerPartNumber = currProduct?.manufacturerPartNumber || '';
    }
    if (requiredList.includes('modelNumber')) {
      initialData[productType].modelNumber = currProduct?.modelNumber || '';
    }
    if (requiredList.includes('color')) {
      initialData[productType].color = currProduct?.color || '';
    }
    if (requiredList.includes('netContent')) {
      initialData[productType].netContent = extractProductNetContent(currProduct.title || currProduct.productName);
    }
    if (requiredList.includes('pieceCount')) {
      initialData[productType].pieceCount = currProduct?.pieceCount || 0;
    }
    if (requiredList.includes('countPerPack')) {
      initialData[productType].countPerPack = parseInt(currProduct?.countPerPack) || parseInt(currProduct?.count) || 1;
    }
    if (requiredList.includes('minimumRecommendedAge')) {
      if (currProduct?.ageRange && currProduct?.ageRange?.toLowerCase()?.includes('up')) {
        initialData[productType].minimumRecommendedAge = currProduct?.ageRange ? extractAgeInfo(currProduct?.ageRange) : {};
      } else if ((currProduct.title || currProduct.productName)?.toLowerCase()?.includes('pack')) {
        initialData[productType].minimumRecommendedAge = (currProduct.title || currProduct.productName) ? extractAgeInfo((currProduct.title || currProduct.productName)) : {};
      }
    }
    if (requiredList.includes("maximumRecommendedAge")) {
      initialData[productType].maximumRecommendedAge = {
        measure: 99,
        unit: "years"
      }
    }
    if (requiredList.includes('batteriesRequired')) {
      initialData[productType].batteriesRequired = currProduct?.batteriesReq === "Y" ? "Yes" : "No";
    }
    if (requiredList.includes('multipackQuantity')) {
      initialData[productType].multipackQuantity = currProduct?.multipackQuantity || parseInt(currProduct?.count) || !(currProduct.title || currProduct.productName)?.toLowerCase()?.includes('pack') ? 1 : null;
    }
    if (requiredList.includes('count')) {
      initialData[productType].count = parseInt(currProduct?.count) || parseInt(currProduct?.countPerPack) || !(currProduct.title || currProduct.productName)?.toLowerCase()?.includes('pack') ? 1 : null;
    }
    if (requiredList.includes('fabricContent')) {
      initialData[productType].fabricContent = currProduct?.fabricContent ? parseFabricContent(currProduct?.fabricContent) : [];
    }
    if (requiredList.includes('volts')) {
      initialData[productType].volts = currProduct?.volts ? parseVoltsContent(currProduct?.volts) : [];
    }
    if (requiredList.includes("recommendedUses")) {
      initialData[productType].recommendedUses = currProduct?.["rec._use"] ? [currProduct?.["rec._use"]] : [];
    }

    const filterRequired = [
      "mainImageUrl", "isProp65WarningRequired", "productName", "keyFeatures", "shortDescription", "prop65WarningText", "assembledProductWeight", "assembledProductDepth",
      "assembledProductHeight", "assembledProductWidth", "brand", "material", "condition", "has_written_warranty", "smallPartsWarnings", "manufacturer", "manufacturerPartNumber",
      "modelNumber", "color", "pieceCount", "countPerPack", "minimumRecommendedAge", "batteriesRequired", "fabricContent", "volts", "recommendedUses", "maximumRecommendedAge"
    ];

    requiredList
      .filter((req) => !filterRequired.includes(req))
      .forEach((field) => {
        const fieldSchema = schema.properties[productType].properties[field];
        const reqType = fieldSchema.type;
        const enumProps = fieldSchema?.items?.enum ?? fieldSchema?.enum;
        const value = currProduct[field];

        const isEnumValid = (val) =>
          !enumProps || (Array.isArray(enumProps) && enumProps.includes(val));

        if (reqType === "string") {
          if (typeof value === "string" && isEnumValid(value)) {
            initialData[productType][field] = value;
          }
        } else if (reqType === "array") {
          const values = Array.isArray(value)
            ? value
            : typeof value === "string"
              ? value.split(", ").map((v) => v.trim())
              : [];

          initialData[productType][field] = values.filter(isEnumValid);
        }
      });

    if (requiredList.includes('colorCategory') && !initialData[productType].colorCategory) {
      initialData[productType].colorCategory = (schema.properties[productType]?.properties?.colorCategory?.items?.enum?.includes(currProduct?.color)) ? [currProduct?.color] : [];
    }

    setFormData(initialData);
  };

  const updateRequiredFields = () => {
    if (!schema || !schema.properties || !schema.properties[productType]) {
      return;
    }

    const newRequiredFields = {};

    // Get properties and required fields list for this product type
    const properties = schema.properties[productType].properties;
    const requiredList = schema.properties[productType].required || [];

    if (properties) {
      newRequiredFields[productType] = {};

      // Add required fields from static list
      requiredList.forEach(fieldName => {
        newRequiredFields[productType][fieldName] = true;
      });

      // Process conditional requirements (allOf conditions)
      const allOfConditions = schema.properties[productType].allOf || [];

      allOfConditions.forEach(condition => {
        // Check if the condition "if" clause matches current form data
        if (condition.if && condition.if.properties && condition.then && condition.then.required) {
          let conditionMet = true;

          // Check each property in the "if" condition
          Object.entries(condition.if.properties).forEach(([fieldName, expectedValue]) => {
            const currentValue = formData[productType]?.[fieldName];

            // If any condition doesn't match, mark as not met
            if (expectedValue.enum) {
              // Handle enum conditions (match any value in enum)
              if (Array.isArray(currentValue)) {
                for (let i = 0; i < currentValue.length; i++) {
                  if (!expectedValue.enum.includes(currentValue[i])) {
                    conditionMet = false;
                    break;
                  }
                }
              } else {
                if (!expectedValue.enum.includes(currentValue)) {
                  conditionMet = false;
                }
              }
            } else if (expectedValue.const !== undefined && currentValue !== expectedValue.const) {
              // Handle const conditions (exact match)
              conditionMet = false;
            } else if (expectedValue?.contains?.enum) {
              if (Array.isArray(currentValue)) {
                for (let i = 0; i < currentValue.length; i++) {
                  if (!expectedValue?.contains?.enum.includes(currentValue[i])) {
                    conditionMet = false;
                    break;
                  }
                }
              } else {
                if (!expectedValue?.contains?.enum.includes(currentValue)) {
                  conditionMet = false;
                }
              }
            }
          });

          // If all conditions are met, add the "then.required" fields
          if (conditionMet) {
            condition.then.required.forEach(fieldName => {
              newRequiredFields[productType][fieldName] = true;
            });
          }
        }
      });
    }

    setRequiredFields(newRequiredFields);
  };

  const handleInputChange = (field, value) => {
    setFormData(prevData => ({
      ...prevData,
      [productType]: {
        ...prevData[productType],
        [field]: value
      }
    }));
  };

  const handleNestedInputChange = (path, fieldName, value) => {
    setFormData(prevData => {
      // Create a deep copy to prevent mutation
      const updatedData = JSON.parse(JSON.stringify(prevData));

      // Start at the productType level
      let target = updatedData[productType];
      if (!target) {
        updatedData[productType] = {};
        target = updatedData[productType];
      }

      // For nested paths, traverse and create the object structure if needed
      if (path) {
        const pathParts = path.split('.');
        for (let i = 0; i < pathParts.length; i++) {
          const part = pathParts[i];

          // Check if this part is an array index
          const isArrayIndex = !isNaN(parseInt(part));

          if (isArrayIndex) {
            // If the target isn't an array yet, create it
            if (!Array.isArray(target)) {
              target = [];
            }

            // Make sure the array has enough elements
            const index = parseInt(part);
            if (index >= target.length) {
              // Fill with null values up to the index
              for (let j = target.length; j <= index; j++) {
                target[j] = null;
              }
            }

            // If we're at the end of the path, this array element will be set later
            // Otherwise, ensure this array element is an object
            if (i < pathParts.length - 1 && (!target[index] || typeof target[index] !== 'object')) {
              target[index] = {};
            }

            target = target[index];
          } else {
            // Regular object property
            if (!target[part]) {
              target[part] = {};
            }
            target = target[part];
          }
        }
      }

      // Set the value at the final location
      target[fieldName] = value;

      return updatedData;
    });
  };

  const handleArrayInputChange = (field, index, value) => {
    setFormData(prevData => {
      // Create a deep copy to prevent mutation
      const updatedData = JSON.parse(JSON.stringify(prevData));

      // Initialize array if it doesn't exist
      if (!updatedData[productType][field]) {
        updatedData[productType][field] = [];
      }
      // Ensure it's an array if it's been converted to an object
      else if (!Array.isArray(updatedData[productType][field])) {
        updatedData[productType][field] = Object.values(updatedData[productType][field]);
      }

      // Update the value at the specified index
      updatedData[productType][field][index] = value;

      return updatedData;
    });
  };

  const addArrayItem = (field) => {
    setFormData(prevData => {
      // Create a deep copy to prevent mutation
      const updatedData = JSON.parse(JSON.stringify(prevData));

      // Initialize array if it doesn't exist
      if (!updatedData[productType][field]) {
        updatedData[productType][field] = [];
      }
      // Ensure it's an array if it's been converted to an object
      else if (!Array.isArray(updatedData[productType][field])) {
        updatedData[productType][field] = Object.values(updatedData[productType][field]);
      }

      // Add an empty item
      updatedData[productType][field].push('');

      return updatedData;
    });
  };

  const removeArrayItem = (field, index) => {
    setFormData(prevData => {
      // Create a deep copy to prevent mutation
      const updatedData = JSON.parse(JSON.stringify(prevData));

      // Remove the item at the specified index if the array exists
      if (updatedData[productType][field]) {
        // Ensure it's an array if it's been converted to an object
        if (!Array.isArray(updatedData[productType][field])) {
          updatedData[productType][field] = Object.values(updatedData[productType][field]);
        }
        updatedData[productType][field].splice(index, 1);
      }

      return updatedData;
    });
  };

  const isFieldRequired = (fieldName) => {
    return requiredFields[productType]?.[fieldName] === true;
  };

  const handleSelectImage = (input) => {
    const fieldSchema = schema.properties[productType].properties[imageField];
    const fieldType = fieldSchema.type;

    if (fieldType === "array") {
      const itemsType = fieldSchema.items?.type;

      if (itemsType === "object") {
        // Find the property that should contain the image URL
        const objectProperties = fieldSchema.items.properties;
        let imageProperty = null;

        // Look for property with format="uri" and type="string"
        for (const [propName, propSchema] of Object.entries(objectProperties)) {
          if (propSchema.type === 'string' && propSchema.format === 'uri') {
            imageProperty = propName;
            break;
          }
        }

        if (imageProperty) {
          // Get current array
          const currentArray = formData[productType]?.[imageField] || [];

          // Create new object with the image URL
          const newImageObject = {};

          // Initialize all properties based on schema
          Object.entries(objectProperties).forEach(([propName, propSchema]) => {
            if (propName === imageProperty) {
              newImageObject[propName] = input;
            } else {
              // Set default values for other properties
              const propType = propSchema.type;
              newImageObject[propName] = propType === 'array' ? [] :
                propType === 'object' ? {} :
                  propType === 'number' || propType === 'integer' ? 0 : '';
            }
          });

          // Add the new object to the array
          setFormData(prevData => {
            const updatedData = JSON.parse(JSON.stringify(prevData));
            if (!updatedData[productType][imageField]) {
              updatedData[productType][imageField] = [];
            }
            updatedData[productType][imageField].push(newImageObject);
            return updatedData;
          });
        }
      } else {
        handleArrayInputChange(imageField, imageIndex, input);
        setImageIndex(prev => prev + 1);
      }
    } else if (fieldType === "string") {
      handleInputChange(imageField, input);
    }
  };

  const renderFormField = (fieldSchema, path, fieldName) => {
    if (!fieldSchema) return null;

    // Check if this field is required
    const required = isFieldRequired(fieldName);

    // Handle array type
    if (fieldSchema.type === 'array') {
      // Get current array values
      let target = formData[productType];
      if (path) {
        const pathParts = path.split('.');
        for (let i = 0; i < pathParts.length && target; i++) {
          target = target[pathParts[i]];
        }
      }

      let arrayValues = target?.[fieldName] || [];

      // Ensure arrayValues is an array
      if (!Array.isArray(arrayValues)) {
        // If it's an object that looks like an array, convert it
        if (typeof arrayValues === 'object' && arrayValues !== null) {
          arrayValues = Object.values(arrayValues);
        } else {
          // If it's something else, make it an empty array
          arrayValues = [];
        }
      }

      const isError = arrayValues.length < fieldSchema.minItems;
      const isObjectArray = fieldSchema.items && fieldSchema.items.type === 'object';

      return (
        <Box mb={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: isError ? theme.accent.error : theme.text.primary, fontFamily: theme.fontFamily, mb: 1 }}>
            {fieldSchema.title || fieldName}
            {required && <span style={{ color: isError ? theme.accent.error : theme.text.primary }}> * </span>}
            (Min items: {fieldSchema.minItems})
          </Typography>

          {arrayValues.map((value, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                border: isObjectArray && `1px solid ${theme.accent.disabled}`,
                borderRadius: '5px',
                p: isObjectArray && 1,
                mb: 2
              }}
            >
              {isObjectArray ? (
                // For array of objects, recursively call renderFormField for each object property
                <Grid container spacing={1} sx={{ flexGrow: 1 }}>
                  {Object.entries(fieldSchema.items.properties).map(([propName, propSchema]) => (
                    <Grid item size={12} key={propName}>
                      {renderFormField(
                        propSchema,
                        `${fieldName}.${index}`,
                        propName
                      )}
                    </Grid>
                  ))}
                </Grid>
              ) : (
                // For array of simple types, use renderSingleField
                <Box sx={{ flexGrow: 1 }}>
                  {renderSingleField(
                    fieldSchema.items,
                    value,
                    (newValue) => handleArrayInputChange(fieldName, index, newValue),
                    `${fieldName}-${index}`,
                    required
                  )}
                </Box>
              )}
              <IconButton
                onClick={() => removeArrayItem(fieldName, index)}
              >
                <CloseIcon sx={{ color: theme.accent.error }} />
              </IconButton>
            </Box>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              if (fieldName.toLowerCase().includes("image")) {
                setImageField(fieldName);
                setOpenImgModal(true);
              } else {
                // Add an empty item based on type
                if (isObjectArray) {
                  // Create an empty object with the structure from fieldSchema.items.properties
                  const emptyObject = {};
                  if (fieldSchema.items && fieldSchema.items.properties) {
                    Object.keys(fieldSchema.items.properties).forEach(propName => {
                      // Initialize with appropriate empty values based on type
                      const propType = fieldSchema.items.properties[propName].type;
                      emptyObject[propName] = propType === 'array' ? [] :
                        propType === 'object' ? {} :
                          propType === 'number' || propType === 'integer' ? 0 : '';
                    });
                  }

                  // Add the empty object to the array
                  setFormData(prevData => {
                    const updatedData = JSON.parse(JSON.stringify(prevData));
                    if (!updatedData[productType][fieldName]) {
                      updatedData[productType][fieldName] = [];
                    } else if (!Array.isArray(updatedData[productType][fieldName])) {
                      updatedData[productType][fieldName] = Object.values(updatedData[productType][fieldName]);
                    }
                    updatedData[productType][fieldName].push(emptyObject);
                    return updatedData;
                  });
                } else {
                  // Use the existing addArrayItem for simple types
                  addArrayItem(fieldName);
                }
              }
            }}
            variant="contained"
            size="small"
            sx={{
              mb: 1,
              textTransform: 'none',
              fontFamily: theme.fontFamily,
              borderRadius: '10px',
              backgroundColor: isError ? theme.accent.error : theme.text.negative,
              color: isError ? theme.text.negative : theme.color.primary,
            }}
          >
            Add {fieldSchema.title || fieldName}
          </Button>
        </Box>
      );
    }

    // Handle object type with nested properties
    if (fieldSchema.type === 'object' && fieldSchema.properties) {
      // Create a new path for nested fields
      const newPath = path ? `${path}.${fieldName}` : fieldName;

      // Get current value
      let target = formData[productType];
      if (path) {
        const pathParts = path.split('.');
        for (let i = 0; i < pathParts.length && target; i++) {
          target = target[pathParts[i]];
        }
      }

      return (
        <Box mb={2}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.text.primary, fontFamily: theme.fontFamily, mb: 1 }}>
            {fieldSchema.title || fieldName}
            {required && <span style={{ color: theme.accent.error }}> *</span>}
          </Typography>

          <Grid container spacing={2}>
            {Object.entries(fieldSchema.properties).map(([propName, propSchema]) => (
              <Grid item size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }} key={propName}>
                {renderFormField(propSchema, newPath, propName)}
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    // Get field value
    let target = formData[productType];
    if (path) {
      const pathParts = path.split('.');
      for (let i = 0; i < pathParts.length && target; i++) {
        target = target[pathParts[i]];
      }
    }
    const value = target?.[fieldName] || '';

    // For basic types, render the appropriate field
    return renderSingleField(
      fieldSchema,
      value,
      (newValue) => {
        if (path) {
          handleNestedInputChange(path, fieldName, newValue);
        } else {
          handleInputChange(fieldName, newValue);
        }
      },
      `${path ? path + '-' : ''}${fieldName}`,
      true
    );
  };

  const renderSingleField = (fieldSchema, value, onChange, fieldId, required = false) => {
    if (!fieldSchema) return null;

    // Handle enum type (dropdown)
    if (fieldSchema.enum) {
      return (
        <>
          {fieldSchema.description ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', }}>
              <CustomSelect
                value={value || ''}
                label={fieldSchema.title || fieldId}
                onChange={(e) => onChange(e.target.value)}
              >
                {fieldSchema.enum.sort().map(option => (
                  <MenuItem key={option} value={option}><Typography sx={{ fontFamily: theme.fontFamily, }}>{option}</Typography></MenuItem>
                ))}
              </CustomSelect>
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography variant="subtitle2" sx={{ color: theme.text.primary, fontFamily: theme.fontFamily }}>
                    {fieldSchema.description.split('-', 2)[1] ?? fieldSchema.description}
                  </Typography>
                }
                arrow
                placement="top"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  },
                }}
              >
                <InfoOutlined sx={{ color: theme.color.primary, fontSize: 24, cursor: 'pointer', }} />
              </HtmlTooltip>
            </Box>
          ) : (
            <CustomSelect
              value={value || ''}
              label={fieldSchema.title || fieldId}
              onChange={(e) => onChange(e.target.value)}
            >
              {fieldSchema.enum.sort().map(option => (
                <MenuItem key={option} value={option}><Typography sx={{ fontFamily: theme.fontFamily, }}>{option}</Typography></MenuItem>
              ))}
            </CustomSelect>
          )}
        </>
      )
    }

    // Handle number or integer type
    if (fieldSchema.type === 'number' || fieldSchema.type === 'integer') {
      return (
        <>
          {fieldSchema.description ? (
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', }}>
              <CustomInput
                fullWidth
                id={fieldId}
                size="small"
                type="number"
                label={fieldSchema.title || fieldId}
                value={value || ''}
                onChange={(e) => {
                  const numValue = fieldSchema.type === 'integer' ? parseInt(e.target.value) : parseFloat(e.target.value);
                  onChange(numValue);
                }}
                isError={required && (value === '' || value === null || value === undefined)}
                InputProps={{
                  min: fieldSchema.minimum,
                  max: fieldSchema.maximum,
                  step: fieldSchema.multipleOf || 1,
                  required: required
                }}
              />
              <HtmlTooltip
                customtheme={theme}
                title={
                  <Typography variant="subtitle2" sx={{ color: theme.text.primary, fontFamily: theme.fontFamily }}>
                    {fieldSchema.description.split('-', 2)[1] ?? fieldSchema.description}
                  </Typography>
                }
                arrow
                placement="top"
                slotProps={{
                  popper: {
                    modifiers: [
                      {
                        name: 'offset',
                        options: {
                          offset: [0, -10],
                        },
                      },
                    ],
                  },
                }}
              >
                <InfoOutlined sx={{ color: theme.color.primary, fontSize: 24, cursor: 'pointer', }} />
              </HtmlTooltip>
            </Box>
          ) : (
            <CustomInput
              fullWidth
              id={fieldId}
              size="small"
              type="number"
              label={fieldSchema.title || fieldId}
              value={value || ''}
              onChange={(e) => {
                const numValue = fieldSchema.type === 'integer' ? parseInt(e.target.value) : parseFloat(e.target.value);
                onChange(numValue);
              }}
              isError={required && (value === '' || value === null || value === undefined)}
              InputProps={{
                min: fieldSchema.minimum,
                max: fieldSchema.maximum,
                step: fieldSchema.multipleOf || 1,
                required: required
              }}
            />
          )}
        </>
      );
    }

    // Handle boolean type (checkbox)
    if (fieldSchema.type === 'boolean') {
      return (
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
              />
            }
            label={
              <>
                {fieldSchema.title || fieldId}
                {required && <span style={{ color: theme.accent.error }}> *</span>}
              </>
            }
          />
        </FormGroup>
      );
    }

    // Default text input for strings and other types
    return (
      <>
        {fieldSchema.description ? (
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', }}>
            <CustomInput
              fullWidth
              id={fieldId}
              size="small"
              type="text"
              label={fieldSchema.title || fieldId}
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              isError={required && (value === '' || value === null)}
              InputProps={{
                maxLength: fieldSchema.maxLength,
                minLength: fieldSchema.minLength,
                required: required,
                endAdornment: fieldId.toLowerCase().includes("image") ? (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => {
                        setImageField(fieldId);
                        setOpenImgModal(true);
                      }}
                      sx={{
                        mr: '-14px',
                        color: required && (value === '' || value === null) ? theme.background.primary : theme.color.primary,
                        fontFamily: theme.fontFamily,
                        backgroundColor: required && (value === '' || value === null) ? theme.accent.error : theme.background.primary,
                      }}
                    >{required && (value === '' || value === null) ? "select" : "change"}</Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <HtmlTooltip
              customtheme={theme}
              title={
                <Typography variant="subtitle2" sx={{ color: theme.text.primary, fontFamily: theme.fontFamily }}>
                  {fieldSchema.description.split('-', 2)[1] ?? fieldSchema.description}
                </Typography>
              }
              arrow
              placement="top"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                },
              }}
            >
              <InfoOutlined sx={{ color: theme.color.primary, fontSize: 24, cursor: 'pointer', }} />
            </HtmlTooltip>
          </Box>
        ) : (
          <CustomInput
            fullWidth
            id={fieldId}
            size="small"
            type="text"
            label={fieldSchema.title || fieldId}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            isError={required && (value === '' || value === null)}
            InputProps={{
              maxLength: fieldSchema.maxLength,
              minLength: fieldSchema.minLength,
              required: required
            }}
          />
        )}
      </>
    );
  };

  function getTypeCategory(schema) {
    // Type 1: String
    if (schema.type === 'string' && !schema.enum) {
      return 1;
    }
    // Type 2: String & Enum
    if (schema.enum) {
      return 2;
    }
    // Type 3: Number
    if (schema.type === 'integer' || schema.type === 'number') {
      return 3;
    }
    // Type 4: Object
    if (schema.type === 'object' || (schema.$schema && schema.type === 'object') ||
      (schema.$schema && schema.properties)) {
      return 4;
    }
    // Type 5: Array
    if (schema.type === 'array' || (schema.$schema && schema.type === 'array') ||
      (schema.$schema && schema.items && schema.minItems)) {
      return 5;
    }

    // Default case
    return 6;
  }

  const renderProductTypeSection = () => {
    if (!schema || !schema.properties || !schema.properties[productType]) {
      return null;
    }

    const properties = schema.properties[productType].properties;
    if (!properties) return null;

    // Get all required and optional fields
    const sectionRequiredFields = requiredFields[productType] || {};

    const requiredProperties = Object.entries(properties).filter(
      ([fieldName]) => sectionRequiredFields[fieldName] === true
    ).sort((a, b) => {
      const typeA = getTypeCategory(a[1]);
      const typeB = getTypeCategory(b[1]);
      return typeA - typeB;
    });;

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: theme.fontFamily, mb: 1 }}>
          Product Type Data ({productType})
        </Typography>
        {requiredProperties.length > 0 && (
          <Grid container spacing={2}>
            {requiredProperties.map(([fieldName, fieldSchema]) => (
              <Grid item size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 3 }} key={fieldName}>
                {renderFormField(fieldSchema, '', fieldName)}
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  if (!schema || !productType) {
    return <Typography>Loading schema...</Typography>;
  }

  return (
    <>
      {renderProductTypeSection()}

      <ImagesModal
        open={openImgModal}
        setOpen={setOpenImgModal}
        images={currProduct.images}
        onSelect={handleSelectImage}
      />
    </>
  );
};

export default SchemaForm;